import type { LngLat, Weather } from './types';

/**
 * Open-Meteo: real, free, no API key, no attribution requirement for
 * non-commercial volume and a cheap commercial tier above it. Chosen so the
 * buggy fleet does not burn a paid quota on 60 units polling all day.
 */
const BASE = 'https://api.open-meteo.com/v1/forecast';
const ELEV = 'https://api.open-meteo.com/v1/elevation';

const CACHE_KEY = 'fairway.weather.v1';
const TTL = 10 * 60 * 1000;

export async function fetchWeather(at: LngLat): Promise<Weather | null> {
  const cached = readCache();
  if (cached && Date.now() - cached.updatedAt < TTL) return cached;

  const url =
    `${BASE}?latitude=${at[1].toFixed(4)}&longitude=${at[0].toFixed(4)}` +
    `&current=temperature_2m,precipitation,weather_code,wind_speed_10m,wind_direction_10m,wind_gusts_10m` +
    `&hourly=precipitation_probability&wind_speed_unit=ms&forecast_days=1`;

  try {
    const r = await fetch(url);
    if (!r.ok) throw new Error(`open-meteo ${r.status}`);
    const j = await r.json();
    const c = j.current;
    const hourIdx = new Date().getHours();
    const w: Weather = {
      tempC: c.temperature_2m,
      windSpeed: c.wind_speed_10m,
      windDir: c.wind_direction_10m,
      gust: c.wind_gusts_10m,
      precipProb: j.hourly?.precipitation_probability?.[hourIdx] ?? 0,
      code: c.weather_code,
      updatedAt: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(w));
    return w;
  } catch {
    return cached; // stale weather beats no weather on a course with no signal
  }
}

function readCache(): Weather | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as Weather) : null;
  } catch {
    return null;
  }
}

/** Batch elevation lookup, used once per course to fill tee/green heights. */
export async function fetchElevations(points: LngLat[]): Promise<number[] | null> {
  if (!points.length) return [];
  const lats = points.map((p) => p[1].toFixed(5)).join(',');
  const lngs = points.map((p) => p[0].toFixed(5)).join(',');
  try {
    const r = await fetch(`${ELEV}?latitude=${lats}&longitude=${lngs}`);
    if (!r.ok) return null;
    const j = await r.json();
    return j.elevation as number[];
  } catch {
    return null;
  }
}

export const WEATHER_TEXT: Record<number, string> = {
  0: 'Clear',
  1: 'Mostly clear',
  2: 'Partly cloudy',
  3: 'Overcast',
  45: 'Fog',
  48: 'Rime fog',
  51: 'Light drizzle',
  53: 'Drizzle',
  55: 'Heavy drizzle',
  61: 'Light rain',
  63: 'Rain',
  65: 'Heavy rain',
  80: 'Showers',
  81: 'Showers',
  82: 'Violent showers',
  95: 'Thunderstorm',
  96: 'Storm + hail',
  99: 'Storm + hail',
};

export function windArrowDeg(windDirFrom: number, mapBearing: number): number {
  return (windDirFrom + 180 - mapBearing + 360) % 360;
}
